import {
    Button,
    Col,
    Dropdown,
    Form,
    Input,
    Layout,
    Row,
    Select,
    Space,
    Table,
    TablePaginationConfig,
    Tag,
    Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DownOutlined } from "@ant-design/icons";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IUserService } from "@shared/interfaces/user.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { Providers } from "@shared/resources/providers";
import { UserProviderType } from "@shared/types/user/enum/user-provider-type.enum";
import {
    BanUserAccountDto,
    ResetUserAccountDto,
    ResetUserDraftsDto,
    UpdateUserDto,
    UserDto,
} from "@shared/types/user/user.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, UseMutationResult, useQuery } from "react-query";
import AlertModal from "./AlertModal/AlertModal";
import ChangeUserModal from "./ChangeUserModal/ChangeUserModal";
import styles from "./Users.module.scss";
import AddTokensBulkModal from "./AddTokensBulkModal/AddTokensBulkModal";

const ALL = "All";

type IsOpenAlertModal =
    null
    | "reset-user"
    | "redeal-cards"
    | "ban"

export type IChangeUserModalType =
    | "draftTokensRemaining"
    | "dailyDraftCounter"
    | "ranking"
    | "streakCount"
    | "dailyDraftTokensCounter";

const items = [
    { key: "dailyDraftCounter", label: "Reset Draft" },
    { key: "dailyDraftTokensCounter", label: "Reset Daily Draft Tokens" },
    { key: "redeal-cards", label: "Re-deal Cards" },
    { key: "update-ranking", label: "Update GLO Ranking" },
    { key: "draftTokensRemaining", label: "Manage Draft Tokens" },
    { key: "streakCount", label: "Manage Streak" },
    { key: "reset-user", label: "Reset User" },
    { key: "ban", label: "Ban User" },
];

const ALL_OBJECT = { label: "All", value: ALL };

const PROVIDERS = [
    { ...ALL_OBJECT, label: "Filter by provider" },
    ...Providers,
];

const getProvider = (type: UserProviderType) => {
    switch (type) {
        case UserProviderType.GOOGLE: {
            return <Tag color="#8b0000">Google</Tag>;
        }

        case UserProviderType.APPLE: {
            return <Tag color="#9400d3">Apple</Tag>;
        }

        case UserProviderType.PHONE: {
            return <Tag color="#aa6c39">Phone</Tag>;
        }

        default:
            return null;
    }
};

const Users = () => {
    const [provider, setProvider] = useState(ALL);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

    const [page, setPage] = useState(1);
    const [currentPageSize, setPageSize] = useState(20);

    const [containerHeight, setContainerHeight] = useState(0);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [modalType, setModalType] =
        useState<IChangeUserModalType>("dailyDraftCounter");
    const [openAddTokensBulkModal, setOpenAddTokensBulkModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [isOpenAlertModal, setIsOpenAlertModal] = useState<IsOpenAlertModal>(null);

    const usersService = appInject<IUserService>(DI_TOKENS.userService);

    const handleMenuItemClick = useCallback((key: string, user: UserDto) => {
        switch (key) {
            case "dailyDraftCounter":
                onOpenUser(user, "dailyDraftCounter");
                break;
            case "dailyDraftTokensCounter":
                onOpenUser(user, "dailyDraftTokensCounter");
                break;
            case "redeal-cards":
                setIsOpenAlertModal("redeal-cards");
                setSelectedUser(user);
                break;
            case "reset-user":
                setIsOpenAlertModal("reset-user");
                setSelectedUser(user);
                break;
            case "ban":
                setIsOpenAlertModal("ban");
                setSelectedUser(user);
                break;
            case "update-ranking":
                onOpenUser(user, "ranking");
                break;
            case "draftTokensRemaining":
                onOpenUser(user, "draftTokensRemaining");
                break;
            case "streakCount":
                onOpenUser(user, "streakCount");
                break;
        }
    }, []);

    const columns = useMemo(
        () =>
            [
                {
                    key: "action",
                    title: "Actions",
                    render: (_: null, user: UserDto) => {
                        return (
                            <Space
                                size="middle"
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                }}
                            >
                                <Dropdown
                                    menu={{
                                        items,
                                        onClick: (e) => {
                                            handleMenuItemClick(e.key, user);
                                        },
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#1677ff",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Actions <DownOutlined />
                                    </div>
                                </Dropdown>
                            </Space>
                        );
                    },
                    width: 130,
                },
                {
                    key: "id",
                    dataIndex: "id",
                    title: "ID",
                    width: 300,
                },
                {
                    key: "nickname",
                    dataIndex: "nickname",
                    title: "Nickname",
                    width: 120,
                },
                {
                    key: "phoneNumber",
                    dataIndex: "phoneNumber",
                    title: "Phone No.",
                    render: (value: number | null) => (value === null ? "N/A" : value),
                    width: 150,
                },
                {
                    key: "ranking",
                    dataIndex: "ranking",
                    title: "Ranking",
                    className: "max-w-[90px]",
                    render: (value: number | null) => (value === null ? "N/A" : value),
                    width: 150,
                },
                {
                    key: "dailyDraftsCounter",
                    dataIndex: "dailyDraftsCounter",
                    title: "Drafts Played",
                    render: (value: number) => value,
                    width: 200,
                },
                {
                    key: "dailyDraftTokensCounter",
                    dataIndex: "dailyDraftTokensCounter",
                    title: "Vs Drafts Played",
                    render: (value: number) => value,
                    width: 200,
                },
                {
                    key: "draftTokensRemaining",
                    dataIndex: "draftTokensRemaining",
                    title: "Drafts Tokens",
                    render: (value: number) => value,
                    width: 200,
                },
                {
                    key: "streakCount",
                    dataIndex: "streakCount",
                    title: "Streak",
                    render: (value: number) => value,
                    width: 200,
                },
                {
                    key: "provider",
                    dataIndex: "provider",
                    title: "Provider",
                    render: getProvider,
                    width: 150,
                },
                {
                    key: "email",
                    dataIndex: "email",
                    title: "Email",
                    render: (value: number | null) => (value === null ? "N/A" : value),
                    width: 150,
                },
                {
                    key: "providerId",
                    dataIndex: "providerId",
                    title: "Provider Id",
                    width: 220,
                    render: (value: number | null) => (value === null ? "N/A" : value),
                },
                {
                    key: "createdAt",
                    dataIndex: "createdAt",
                    title: "CreatedAt",
                    render: (date: Date | null) =>
                        date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
                    width: 200,
                },
            ] as ColumnsType<UserDto>,
        [handleMenuItemClick],
    );

    const usersQuery = useQuery(
        [
            "download-user-list",
            page,
            currentPageSize,
            debouncedSearchText,
            provider,
        ],
        () =>
            usersService.getUsersList({
                page,
                pageSize: currentPageSize,
                query: debouncedSearchText,
                provider: (provider === ALL
                    ? undefined
                    : (provider ?? undefined)) as UserProviderType,
            }),
        {
            refetchOnMount: true,
        },
    );

    const updateUserMutation: UseMutationResult<
        UserDto,
        unknown,
        UpdateUserDto,
        unknown
    > = useMutation(
        (data: UpdateUserDto): Promise<UserDto> => usersService.update(data),
        {
            onSuccess: () => {
                setSelectedUser(null);
                setIsOpenModal(false);
                usersQuery.refetch();
            },
        },
    );

    const resetUserDraftsMutation: UseMutationResult<
        unknown,
        unknown,
        ResetUserDraftsDto,
        unknown
    > = useMutation(
        (data: ResetUserDraftsDto): Promise<any> => usersService.reDealCards(data),
        {
            onSuccess: () => {
                setSelectedUser(null);
                setIsOpenAlertModal(null);
                usersQuery.refetch();
            },
        },
    );

    const resetUserAccountMutation: UseMutationResult<
        unknown,
        unknown,
        ResetUserAccountDto,
        unknown
    > = useMutation(
        (data: ResetUserAccountDto): Promise<any> =>
            usersService.resetUserAccount(data),
        {
            onSuccess: () => {
                setSelectedUser(null);
                setIsOpenAlertModal(null);
                usersQuery.refetch();
            },
        },
    );
    
    const banUserAccountMutation: UseMutationResult<
        unknown,
        unknown,
        BanUserAccountDto,
        unknown
    > = useMutation(
        (data: BanUserAccountDto): Promise<any> =>
            usersService.banUserAccount(data),
        {
            onSuccess: () => {
                setSelectedUser(null);
                setIsOpenAlertModal(null);
                usersQuery.refetch();
            },
        },
    );

    const handleCloseDialog = () => {
        setIsOpenModal(false);
        setSelectedUser(null);
    };

    const handleCloseAlertDialog = () => {
        resetUserDraftsMutation.reset();
        setIsOpenAlertModal(null);
    };

    const handleSaveDialog = (data: UserDto) => {
        if (selectedUser?.id) {
            updateUserMutation.mutate({ ...data, id: selectedUser.id });
        }
    };

    const handleProceedDialog = (data: UserDto) => {

        if (!isOpenAlertModal || !selectedUser) {
            return '';
        }

        const keyWiseMutations: Record<Exclude<IsOpenAlertModal, null>, Function> = {
            "ban": banUserAccountMutation.mutate,
            "redeal-cards": resetUserDraftsMutation.mutate,
            "reset-user": resetUserAccountMutation.mutate,
        }

        keyWiseMutations[isOpenAlertModal]({ ...data, id: selectedUser.id })

    };

    const containerRef = useRef<any>();

    const onOpenUser = (user: UserDto, type: IChangeUserModalType) => {
        setSelectedUser(user);
        setModalType(type);
        setIsOpenModal(true);
    };

    const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
        setPage(current || 1);
        if (pageSize !== currentPageSize) {
            setPageSize(pageSize || 20);
            setPage(1);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            if ("offsetHeight" in containerRef.current) {
                setContainerHeight(containerRef.current.offsetHeight - 140);
            }
        }, 100);
    }, []);

    // Update debounced search text after a delay
    useEffect(() => {
        const handler = setTimeout(() => {
            setPage(1);
            setDebouncedSearchText(searchText);
        }, 300); // Adjust debounce delay as needed

        // Clear the timeout if the effect re-runs (when searchText changes)
        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    const handleSearch = (e: any) => {
        setSearchText(e.target.value);
    };

    const getErrorMessage = (error: any) => {
        if (error) {
            if (error instanceof HttpErrorResponse) {
                return error.errors.get("message" as any) as unknown as string;
            }
            return `Player doesnt have a full set of cards available right now`;
        }
    };

    const resetFilters = () => {
        setSearchText("");
        setProvider(ALL);
    };

    const openAddTokensBulkModalFn = () => setOpenAddTokensBulkModal(true);
    const closeAddTokensBulkModalFn = () => setOpenAddTokensBulkModal(false);

    const getAlertMessage = (username: string) => {

        if (!isOpenAlertModal) {
            return '';
        }

        const keyMessageObj: Record<Exclude<IsOpenAlertModal, null>, string> = {
            "ban": `Are you sure you want to ban ${selectedUser?.nickname} user?`,
            "redeal-cards": `Are you sure you want to re-deal the draft cards of ${selectedUser?.nickname} user?`,
            "reset-user": `Are you sure you want to reset the account of ${selectedUser?.nickname} user?`,
        }
        return keyMessageObj[isOpenAlertModal] || `Are you sure you want to proceed with this action for ${username}?`;
    }

    return (
        <Layout className={styles.tableLayout}>
            {isOpenModal ? (
                <ChangeUserModal
                    open={isOpenModal}
                    user={selectedUser}
                    onClose={handleCloseDialog}
                    onSave={handleSaveDialog}
                    isLoading={updateUserMutation.isLoading}
                    isError={updateUserMutation.isError}
                    type={modalType}
                />
            ) : (
                <></>
            )}

            {openAddTokensBulkModal ? (
                <AddTokensBulkModal
                    open={true}
                    onClose={closeAddTokensBulkModalFn}
                    onSuccess={() => {
                        usersQuery.refetch();
                        closeAddTokensBulkModalFn();
                    }}
                />
            ) : null}

            {isOpenAlertModal ? (
                <AlertModal
                    open={Boolean(isOpenAlertModal)}
                    description={getAlertMessage(selectedUser?.nickname || "")}
                    onClose={handleCloseAlertDialog}
                    isLoading={updateUserMutation.isLoading || banUserAccountMutation.isLoading || resetUserDraftsMutation.isLoading}
                    error={getErrorMessage(resetUserDraftsMutation.error)}
                    onProceed={handleProceedDialog}
                />
            ) : (
                <></>
            )}

            <Row
                style={{
                    height: 32,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                }}
            >
                <Col
                    span={7}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        rowGap: 5,
                        columnGap: 5,
                        height: "100%",
                    }}
                >
                    <Typography.Title style={{ margin: 0 }} level={5}>
                        Users ({usersQuery.data?.total})
                    </Typography.Title>

                    <Button type="primary" onClick={openAddTokensBulkModalFn}>
                        Assign Bulk Tokens
                    </Button>
                </Col>
                <Col>
                    <Form.Item className="search-input-container">
                        <Input
                            onChange={handleSearch}
                            value={searchText}
                            name="search"
                            style={{
                                minWidth: 200,
                            }}
                            placeholder="Search by Name , Phone number"
                        />

                        <Select
                            style={{
                                minWidth: 150,
                            }}
                            options={PROVIDERS}
                            value={provider}
                            onChange={setProvider}
                        />

                        <Button type="primary" onClick={resetFilters}>
                            Reset
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
            <Layout.Content
                ref={containerRef}
                className={styles.content}
                style={{ backgroundColor: "white" }}
            >
                <Table
                    rowKey="id"
                    dataSource={usersQuery.data?.data || []}
                    onChange={onTableChange}
                    columns={columns}
                    loading={usersQuery.isFetching}
                    pagination={{
                        current: page,
                        total: usersQuery.data?.total || 0,
                        pageSize: currentPageSize,
                        showSizeChanger: true,
                    }}
                    size="small"
                    scroll={{ y: containerHeight, x: "max-content" }}
                />
            </Layout.Content>
        </Layout>
    );
};

export default Users;
